import { Injectable, inject } from '@angular/core';
import { UserStore } from '../../../features/user/store/user.store';
import { EnvironmentService } from 'app/shared/services/environment.service';
import { effect } from '@angular/core';

export interface PaymentConfigInterface {
  precision: number;
  calculator: {
    maxTaxPercent: number;
    maxCapitalPercent: number;
    capital: {
      percent: number;
    }
  };
}

interface PaymentTotals {
  subtotal: string;
  total: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentCalculatorService {
  private userStore = inject(UserStore);
  
  config: PaymentConfigInterface = {
    precision: 2,
    calculator: {
      maxTaxPercent: 0,
      maxCapitalPercent: 0,
      capital: {
        percent: 0
      }
    }
  };

  constructor(
    protected environmentService: EnvironmentService
  ) {
    effect(() => {
      const activeProfile = this.userStore.activeProfile();
      const costOfCapital = activeProfile?.profileDetail?.profileAdminSettings?.costOfCapitalPercent || this.environmentService.get('config.payment.calculator.maxCapitalPercent');
      
      this.config = {
        precision: 2,
        calculator: {
          maxTaxPercent: this.environmentService.get('config.payment.calculator.maxTaxPercent'),
          maxCapitalPercent: costOfCapital,
          capital: {
            percent: costOfCapital
          }
        }
      };
    });
  }

  calculateCapital(amount: number, costOfCapitalPercentOverride?: number) {
    const activeProfile = this.userStore.activeProfile();
    this.config.calculator.capital.percent = activeProfile?.profileDetail?.profileAdminSettings?.costOfCapitalPercent || this.environmentService.get('config.payment.calculator.maxCapitalPercent');
    this.config.calculator.maxCapitalPercent = this.config.calculator.capital.percent;

    let percent = this.config.calculator.capital.percent;
    percent = costOfCapitalPercentOverride || percent;
    let maxAmount = 0;
    // First calculate the maximum allowed amount
    if (costOfCapitalPercentOverride) {
      maxAmount = Math.floor(amount * costOfCapitalPercentOverride) / 100;
      this.config.calculator.maxCapitalPercent = costOfCapitalPercentOverride;
    } else {
      maxAmount = Math.floor(amount * this.config.calculator.maxCapitalPercent) / 100;
      this.config.calculator.maxCapitalPercent = this.config.calculator.maxCapitalPercent;
    }
    // Then calculate the requested percentage
    const calculated = Math.floor(amount * percent * 100) / 10000;
    // Take the smaller value and ensure it's rounded down
    return this.round(Math.floor(Math.min(calculated, maxAmount) * 100) / 100);

  }

  getMaxTaxPercent() {
    return this.config.calculator.maxTaxPercent;
  }

  isTaxAmountExceeded(invoiceAmount: number, taxAmount: number) {
    return taxAmount > invoiceAmount * this.getMaxTaxPercent() / 100;
  }

  getMaxCapitalPercent() {
    return this.config.calculator.maxCapitalPercent;
  }

  isCapitalAmountExceeded(invoiceAmount: number, capitalAmount: number) {
    return capitalAmount > invoiceAmount * this.getMaxCapitalPercent() / 100;
  }

  round(value: number) {
    return Number(value).toFixed(this.config.precision);
  }

  calculateTotals(amounts: {
    invoiceTotal: number;
    taxAmount: number;
    shippingAmount: number;
    capitalAmount: number;
    otherAmount: number;
  }): PaymentTotals {
    const subtotal = amounts.invoiceTotal - amounts.taxAmount - amounts.shippingAmount;
    const total = amounts.invoiceTotal + amounts.capitalAmount + amounts.otherAmount;

    return {
      subtotal: this.round(subtotal),
      total: this.round(total)
    };
  }
}
